import {
    Fragment as ReactFragment,
    ReactNode,
    useRef,
    useEffect,
    useCallback,
    useState,
} from 'react';
import {
    HStack,
    VStack,
    HStackProps,
    ButtonProps,
    IconButton,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Menu,
    MenuTrigger,
    MenuContent,
    MenuItem,
} from '@cad-arc';
import { useDrawerRender } from '../../drawers/components/drawer-context-provider';
import { ResponsiveButton } from '../types';

export function ResponsiveButtons({
    buttons,
    containerProps,
    moreActionsButtonProps,
    overflowVariant = 'popover',
}: {
    buttons: ResponsiveButton[];
    containerProps?: HStackProps;
    moreActionsButtonProps?: ButtonProps;
    overflowVariant?: 'popover' | 'menu';
}) {
    const ref = useRef<HTMLDivElement>(null);
    const drawerRender = useDrawerRender();
    const [displayedButtons, setDisplayedButtons] = useState<ReactNode[]>([]);
    const [overflowButtons, setOverflowButtons] = useState<ResponsiveButton[]>([]);
    const showOverflowButton = overflowButtons.length > 0;

    const updateDisplayedButtons = useCallback(() => {
        const containerWidth = ref.current?.getBoundingClientRect().width ?? Infinity;
        const displayed: ReactNode[] = [];
        const overflow: ResponsiveButton[] = [];

        for (const button of buttons) {
            if (containerWidth >= button.breakpoint) {
                displayed.push(button.component);
            } else {
                overflow.unshift(button);
            }
        }

        setDisplayedButtons(displayed);
        setOverflowButtons(overflow);
    }, [buttons]);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        updateDisplayedButtons();
        window.addEventListener('resize', updateDisplayedButtons);

        return () => {
            window.removeEventListener('resize', updateDisplayedButtons);
        };
    }, [updateDisplayedButtons, drawerRender]);

    return (
        <HStack width="100%" gap="5px" ref={ref} {...containerProps}>
            {displayedButtons.map((button, index) => (
                <ReactFragment key={index}>{button}</ReactFragment>
            ))}
            {showOverflowButton && overflowVariant === 'popover' && (
                <Popover>
                    <PopoverTrigger>
                        <IconButton icon="MoreActions" {...moreActionsButtonProps} />
                    </PopoverTrigger>
                    <PopoverContent>
                        <VStack alignItems="center" gap="10px">
                            {overflowButtons.map((button, index) => (
                                <ReactFragment key={index}>{button.component}</ReactFragment>
                            ))}
                        </VStack>
                    </PopoverContent>
                </Popover>
            )}
            {showOverflowButton && overflowVariant === 'menu' && (
                <Menu>
                    <MenuTrigger asChild>
                        <IconButton
                            icon="MoreActions"
                            aria-label="more actions"
                            {...moreActionsButtonProps}
                        />
                    </MenuTrigger>
                    <MenuContent>
                        {overflowButtons.map(
                            (button, index) =>
                                button.menuItemLabel && (
                                    <MenuItem
                                        key={index}
                                        leadingVisual="Add"
                                        disabled={button.menuItemDisabled}
                                        onSelect={button.menuItemOnSelect}
                                    >
                                        {button.menuItemLabel}
                                    </MenuItem>
                                )
                        )}
                    </MenuContent>
                </Menu>
            )}
        </HStack>
    );
}
